<template>
  <section class="limits-usage-view" data-testid="limits-usage-view">
    <!-- Header -->
    <div class="flex flex-col items-start gap-5 px-5 py-5">
      <!-- Icon -->
      <img src="./crown-icon.svg" alt="" class="w-11 h-11" />

      <!-- Title and Description -->
      <div class="flex flex-col items-start gap-1 w-full">
        <h2 class="text-lg font-semibold text-zinc-900 dark:text-zinc-100 leading-7">
          {{ headingText }}
        </h2>
        <p class="text-sm text-zinc-600 dark:text-zinc-300">
          {{ descriptionText }}
        </p>
      </div>
    </div>

    <!-- Usage Progress Bars -->
    <div class="flex flex-col px-5" v-if="usage.length">
      <div v-for="(limit, index) in usage" :key="limit.label" :class="{ 'mb-5': index < usage.length - 1 }">
        <LimitProgressBar
          :label="limit.label"
          :current="limit.current"
          :max="limit.max"
          :disabled="limit.disabled"
          :disabled-note="limit.disabledNote"
          :note="limit.note"
        />
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import LimitProgressBar from './LimitProgressBar.vue';
import type { LimitUsage } from './types';

interface Props {
  usage: LimitUsage[];
  plan?: 'free' | 'create' | 'collaborate' | 'enterprise';
}

const props = withDefaults(defineProps<Props>(), {
  plan: 'free',
});

const hasReachedLimit = computed(() => {
  return props.usage.some(limit => !limit.disabled && limit.max > 0 && limit.current >= limit.max);
});

const highestUsagePercentage = computed(() => {
  return props.usage.filter(limit => !limit.disabled && limit.max > 0).reduce((highest, limit) => Math.max(highest, (limit.current / limit.max) * 100), 0);
});

const headingText = computed(() => {
  if (props.plan === 'collaborate' || props.plan === 'enterprise') {
    return 'Your workspace can scale with your team';
  }
  if (props.plan === 'free' && hasReachedLimit.value) {
    return 'Unlock more ways to get work done';
  }
  if (!hasReachedLimit.value && highestUsagePercentage.value < 50) {
    return 'Your workspace has limits';
  }
  return hasReachedLimit.value ? 'Your workspace has reached its limit' : 'Your workspace is close to hitting a usage limit';
});

const descriptionText = computed(() => {
  if (props.plan === 'collaborate' || props.plan === 'enterprise') {
    return 'Provide your whole team with deeper integrations, analytics and controls to supercharge your workflow. Grow collaboration within and outside your team.';
  }
  if (props.plan === 'create') {
    return 'Unlock AI automation, advanced editing tools, deeper analytics, team collaboration, integrations and more to supercharge your workflow.';
  }
  return 'Get unlimited recordings, AI automation, advanced editing tools, deeper analytics, integrations and more to supercharge your workflow.';
});
</script>

<style scoped>
.limits-usage-view {
  @apply bg-white dark:bg-zinc-800 w-full;
}
</style>
